/**
 * Frontend adapter for backend Security Health Scores.
 *
 * The backend is the single source of truth: nothing here calculates a score,
 * a grade, or a label from vulnerability counts. Every value is read as-is
 * from the API response and normalised into one shape per scope:
 *
 *   { key, status, score, grade, label, formulaVersion, findingCount,
 *     topRiskDrivers, dataQuality, message }
 *
 * Today's `/dashboard/overview/kpis/` only exposes flat `health_*` fields for
 * the overall score. Per-scope values (network / web / internal / external)
 * are not exposed yet, so those scopes resolve to `unavailable`.
 * `readScopeSource` is the only place that knows the response layout and is
 * where the final API contract gets wired in.
 */

export const HEALTH_SCOPE_KEYS = Object.freeze(['overall', 'network', 'web', 'internal', 'external']);

export const HEALTH_SCOPE_STATUS = Object.freeze({
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
  INSUFFICIENT_DATA: 'insufficient_data',
  ERROR: 'error',
});

const KNOWN_STATUSES = new Set(Object.values(HEALTH_SCOPE_STATUS));

function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '')) {
      return value;
    }
  }
  return undefined;
}

function toFiniteNumber(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toText(value) {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text || null;
}

function toBooleanOrNull(value) {
  if (value === true || value === false) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
}

/**
 * Maps the current response layout onto a raw per-scope object.
 * Returns null when the backend does not expose that scope.
 */
function readScopeSource(summary, key, healthV12 = false) {
  if (!summary || typeof summary !== 'object') return null;

  const v12Scopes = healthV12 && summary.security_health_v12 && typeof summary.security_health_v12 === 'object'
    ? summary.security_health_v12
    : null;
  const v12Scope = v12Scopes && v12Scopes[key] && typeof v12Scopes[key] === 'object'
    ? v12Scopes[key]
    : null;
  if (v12Scope) {
    return {
      score: pick(v12Scope, 'score'),
      grade: pick(v12Scope, 'grade'),
      label: pick(v12Scope, 'label'),
      formula_version: pick(v12Scope, 'formula_version', 'formulaVersion'),
      finding_count: pick(v12Scope, 'finding_count', 'findingCount'),
      top_risk_drivers: pick(v12Scope, 'top_risk_drivers', 'topRiskDrivers'),
      data_quality: pick(v12Scope, 'data_quality', 'dataQuality'),
      diagnostics: pick(v12Scope, 'diagnostics'),
      status: pick(v12Scope, 'status'),
      message: pick(v12Scope, 'message'),
      applicable: pick(v12Scope, 'applicable', 'is_applicable'),
    };
  }

  if (healthV12) {
    // When v1.2 is enabled, do not mix legacy overall scoring into v1.2 scope UI.
    return null;
  }

  if (key === 'overall') {
    return {
      score: pick(summary, 'health_score', 'healthScore'),
      grade: pick(summary, 'health_grade', 'healthGrade'),
      label: pick(summary, 'health_label', 'healthLabel'),
      formula_version: pick(summary, 'health_formula_version', 'healthFormulaVersion'),
      finding_count: pick(summary, 'health_finding_count', 'healthFindingCount'),
      top_risk_drivers: pick(summary, 'health_top_drivers', 'healthTopDrivers'),
      data_quality: pick(summary, 'health_data_quality', 'healthDataQuality'),
      status: pick(summary, 'health_status', 'healthStatus'),
    };
  }
  return null;
}

/**
 * Normalises one backend risk driver into the finding/asset-level shape.
 * Returns null for legacy severity-group drivers (e.g. "High findings") that
 * lack a finding identifier — those stay hidden until v1.1 supplies them.
 * Never invents ids or driver rows.
 */
export function normalizeRiskDriver(raw) {
  if (!raw || typeof raw !== 'object') return null;
  // Require a real finding/vuln id so severity-group aggregates are skipped.
  const id = toText(pick(raw, 'id', 'finding_id', 'findingId', 'vuln_id', 'vulnId'));
  if (!id) return null;
  const title = toText(pick(raw, 'title', 'name', 'finding', 'driver'));
  if (!title) return null;
  const severity = toText(pick(raw, 'severity', 'severity_label'));
  const tags = Array.isArray(raw.tags)
    ? raw.tags.map(toText).filter(Boolean)
    : [];
  return {
    id,
    title,
    asset: toText(pick(raw, 'asset', 'asset_name', 'host', 'hostname', 'target', 'url')),
    link: toText(pick(raw, 'link', 'url', 'path', 'href', 'finding_url', 'findingUrl')),
    severity,
    tags,
    contributionPct: toFiniteNumber(pick(raw, 'contribution_pct', 'contributionPct', 'contribution')),
    riskPoints: toFiniteNumber(pick(raw, 'risk_points', 'riskPoints')),
    count: toFiniteNumber(pick(raw, 'count', 'finding_count')),
    reason: toText(pick(raw, 'reason', 'explanation')),
  };
}

/** Normalises backend coverage/confidence info; returns null when absent. */
export function normalizeDataQuality(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const coveragePct = toFiniteNumber(
    pick(raw, 'coverage_pct', 'coveragePct', 'coverage', 'overall_percent', 'overallPercent')
  );
  const rawBreakdown = raw.breakdown;
  let breakdown = [];
  if (Array.isArray(rawBreakdown)) {
    breakdown = rawBreakdown
      .map((item, idx) => {
        const label = toText(pick(item, 'label', 'name', 'key'));
        const pct = toFiniteNumber(pick(item, 'coverage_pct', 'coveragePct', 'coverage', 'value'));
        return label && pct != null ? { key: toText(item?.key) || `dq-${idx}`, label, pct } : null;
      })
      .filter(Boolean);
  } else if (rawBreakdown && typeof rawBreakdown === 'object') {
    breakdown = Object.entries(rawBreakdown)
      .map(([key, value]) => {
        const pct = toFiniteNumber(value && typeof value === 'object' ? pick(value, 'coverage_pct', 'coverage', 'value') : value);
        const label = toText(value && typeof value === 'object' ? pick(value, 'label', 'name') : null)
          || key.replace(/_/g, ' ');
        return pct != null ? { key, label, pct } : null;
      })
      .filter(Boolean);
  }
  const confidence = toText(pick(raw, 'confidence', 'confidence_label'));
  if (coveragePct == null && breakdown.length === 0 && !confidence) return null;
  return { coveragePct, confidence, breakdown };
}

function emptyScope(key, status, message = null) {
  return {
    key,
    status,
    score: null,
    grade: null,
    label: null,
    formulaVersion: null,
    findingCount: null,
    topRiskDrivers: [],
    dataQuality: null,
    diagnostics: null,
    message,
    applicable: true,
  };
}

function resolveScope(key, raw, { isLoading, errorMessage }) {
  if (!raw) {
    if (isLoading) return emptyScope(key, HEALTH_SCOPE_STATUS.LOADING);
    if (errorMessage && key === 'overall') return emptyScope(key, HEALTH_SCOPE_STATUS.ERROR, errorMessage);
    return emptyScope(key, HEALTH_SCOPE_STATUS.UNAVAILABLE);
  }

  const score = toFiniteNumber(raw.score);
  const drivers = Array.isArray(raw.top_risk_drivers)
    ? raw.top_risk_drivers.map(normalizeRiskDriver).filter(Boolean)
    : [];
  const base = {
    key,
    score,
    grade: toText(raw.grade),
    label: toText(raw.label),
    formulaVersion: toText(raw.formula_version),
    findingCount: toFiniteNumber(raw.finding_count),
    topRiskDrivers: drivers,
    dataQuality: normalizeDataQuality(raw.data_quality),
    diagnostics: raw.diagnostics && typeof raw.diagnostics === 'object' ? raw.diagnostics : null,
    message: toText(raw.message),
    applicable: toBooleanOrNull(raw.applicable) ?? true,
  };

  const explicitStatus = toText(raw.status)?.toLowerCase();
  if (explicitStatus && KNOWN_STATUSES.has(explicitStatus) && explicitStatus !== HEALTH_SCOPE_STATUS.READY) {
    return { ...base, score: null, status: explicitStatus };
  }
  if (score != null) return { ...base, status: HEALTH_SCOPE_STATUS.READY };
  if (isLoading) return { ...base, status: HEALTH_SCOPE_STATUS.LOADING };
  if (errorMessage) return { ...base, status: HEALTH_SCOPE_STATUS.ERROR, message: base.message || errorMessage };
  return { ...base, status: HEALTH_SCOPE_STATUS.UNAVAILABLE };
}

/**
 * Today's backend returns score 100 (exp(0)) for tenants with no findings and
 * no assets. Treat that as insufficient data rather than a perfect score.
 * This is a presence check only; it never produces a score.
 */
function hasNoPostureData(summary) {
  if (!summary || typeof summary !== 'object') return false;
  const count = (...keys) => toFiniteNumber(pick(summary, ...keys)) || 0;
  const findings =
    count('total_count_site', 'total_open')
    + count('critical_count_site', 'critical')
    + count('high_count_site', 'high')
    + count('medium_count_site', 'medium')
    + count('low_count_site', 'low')
    + count('close_count_site', 'total_closed')
    + count('accepted_count_site', 'total_accepted')
    + count('false_positive_count_site', 'false_count_site', 'total_false_positive');
  const assets =
    count('total_network', 'net_count_site')
    + count('total_web', 'web_count_site')
    + count('endpoints_count', 'endpoint_count');
  return findings === 0 && assets === 0;
}

/**
 * @param {object|null} summary  Overview KPI payload (`getDashboardSummary()`).
 * @param {{ isLoading?: boolean, error?: string|Error|null, healthV12?: boolean }} [options]
 * @returns {Record<'overall'|'network'|'web'|'internal'|'external', object>}
 */
export function selectHealthScopes(summary, { isLoading = false, error = null, healthV12 = false } = {}) {
  const summaryReady = summary != null && typeof summary === 'object';
  const errorMessage =
    toText(error instanceof Error ? error.message : error)
    || (summaryReady ? toText(summary._dashboardKpisError) : null)
    || (summaryReady && summary._dashboardKpisUnauthorized ? 'Sign in again to load the health score.' : null);
  const loading = Boolean(isLoading) || (!summaryReady && !errorMessage);
  const noPostureData = summaryReady && !healthV12 && hasNoPostureData(summary);

  const scopes = {};
  for (const key of HEALTH_SCOPE_KEYS) {
    const raw = summaryReady ? readScopeSource(summary, key, healthV12) : null;
    let scope = resolveScope(key, raw, { isLoading: loading, errorMessage });
    if (noPostureData && scope.status === HEALTH_SCOPE_STATUS.READY) {
      scope = { ...scope, score: null, status: HEALTH_SCOPE_STATUS.INSUFFICIENT_DATA };
    }
    scopes[key] = scope;
  }
  return scopes;
}
